import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useUpdateCoverageMutation } from '@/services/api/coverageService';
import {
  CoverageCategory,
  type CoverageListItem,
  CoverageStatus,
} from '@/services/types/coverage-types';

import { CoverageActionButtons } from './CoverageActionButtons';
import { SelectableCoverageList } from './SelectableCoverageList';

interface CoverageSelectionManagerProps {
  coverages: CoverageListItem[];
  matterId: string;
  onCoverageView: (coverageId: string) => void;
  onUpdate: () => void;
  filterCategory?: CoverageCategory;
  onFilterChange: (category: CoverageCategory | undefined) => void;
}

export const CoverageSelectionManager = ({
  coverages,
  matterId,
  onCoverageView,
  onUpdate,
  filterCategory,
  onFilterChange,
}: CoverageSelectionManagerProps) => {
  const navigate = useNavigate();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [updateCoverage, { isLoading: isDeleting }] =
    useUpdateCoverageMutation();

  const handleSelectionChange = (coverageIds: string[]) => {
    setSelectedIds(coverageIds);
  };

  const handleDeleteSelected = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      // Delete all coverages in parallel
      await Promise.all(
        selectedIds.map((coverageId) =>
          updateCoverage({
            matterId,
            coverageId,
            coverage: { status: CoverageStatus.REJECTED },
          }).unwrap(),
        ),
      );

      toast.success(
        `Successfully rejected ${selectedIds.length} coverage${selectedIds.length === 1 ? '' : 's'}`,
      );

      setSelectedIds([]);
      setShowDeleteConfirm(false);
      onUpdate();
    } catch (error) {
      toast.error('Failed to reject some coverages');
    }
  };

  const handleMergeSelected = () => {
    if (selectedIds.length < 2) {
      toast.error('Please select at least 2 coverages to merge');
      return;
    }

    // Navigate to merge page with selected coverage IDs
    const coverageIds = selectedIds.join(',');
    const url = `/matters/${matterId}/coverage/merge?ids=${coverageIds}`;

    navigate(url);
  };

  return (
    <>
      <CoverageActionButtons
        selectedCount={selectedIds.length}
        onDeleteSelected={handleDeleteSelected}
        onMergeSelected={handleMergeSelected}
        isDeleting={isDeleting}
      />

      <div className="flex-1 overflow-y-auto px-6 py-6">
        <SelectableCoverageList
          coverages={coverages}
          selectedCoverageIds={selectedIds}
          onSelectionChange={handleSelectionChange}
          filterCategory={filterCategory}
          onFilterChange={onFilterChange}
        />
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Selected Coverages</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedIds.length} coverage
              {selectedIds.length === 1 ? '' : 's'}? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
